/**
 * AdminLayout - Shared layout for the agency pages (Tasks, Clients, Leads, Places, Setup)
 * Provides the same structure as MainApp but without client context
 */

import React from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { AuthGuard } from "@/components/Auth/AuthGuard";
import { UserMenu } from "@/components/Header/UserMenu";
import { PhoneNavSheet } from "@/components/Header/PhoneNavSheet";
import { TabGroup } from "@/components/ui/TabGroup";
import { useAppStore } from "@/core/store/useAppStore";
import { ADMIN_CANVAS, useCanvasColor } from "@/hooks/useCanvasColor";
import { NAV_TABS, NAV_TAB_CONFIG } from "./adminNav";

export function AdminLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  // The status-bar strip and the page behind the shell match its gray (spec C3).
  useCanvasColor(ADMIN_CANVAS);

  const user = useAppStore((state) => state.user);
  const activeTab = NAV_TABS.find(t => t.id === location.pathname)?.id || "/tasks";

  return (
    <AuthGuard>
      {/* `relative` makes the shell the containing block, so absolutely
          positioned descendants (sr-only labels) are clipped here instead
          of stretching the document; `h-dvh` is the visible height on iOS.
          `overflow-clip`, not `hidden`: hidden is still a scroll container,
          so code (scrollIntoView, focus) could scroll the shell and push the
          header off screen where the user cannot scroll it back. */}
      <div className="relative h-dvh flex flex-col bg-gray-100 overflow-clip">
        {/* Header — transparent on the gray canvas (DD-011). */}
        <header className="shrink-0 h-14 px-4 lg:px-6 flex items-center justify-between">
          {/* Below md a burger opens the section sheet (DD-057); from md up, the tab strip. */}
          <div className="flex items-center gap-4 min-w-0">
            <PhoneNavSheet title="Menu" items={NAV_TABS} activeId={activeTab} onSelect={(id) => navigate(id)} />
            <nav className="hidden md:block min-w-0 overflow-x-auto">
              <TabGroup
                items={NAV_TABS}
                activeId={activeTab}
                onTabChange={(id) => navigate(id)}
                config={NAV_TAB_CONFIG}
              />
            </nav>
          </div>
          {/* Route pages portal their primary action here (ClientsView's
              New Client) so it sits in the header, not the page body. */}
          <div className="flex items-center gap-3 shrink-0">
            <div id="admin-header-actions" className="flex items-center gap-3" />
            {user && <UserMenu user={user} />}
          </div>
        </header>

        {/* Main content */}
        <main className="flex-1 overflow-hidden">
          <Outlet />
        </main>
      </div>
    </AuthGuard>
  );
}
